/**
 * Sentiment Analysis for Avatar Mood Detection
 * Analyzes conversation utterances to determine appropriate avatar mood/emotion
 * Initial implementation uses keyword matching, can be enhanced with AI integration
 */

export type Sentiment = 'positive' | 'negative' | 'neutral' | 'confused' | 'concerned' | 'empathetic' | 'celebration' | 'concentration' | 'surprised' | 'frustrated';

export interface SentimentResult {
  sentiment: Sentiment;
  confidence: number;
  keywords: string[];
}

/**
 * Which sentiment producers may drive the mood overlay (Aug 17 2026):
 * - 'server' — runtime `visual.sentiment` events only (producer A LLM text +
 *   voice prosody + aiSentiment node). The production default.
 * - 'client' — the on-device keyword analyzer in this module only.
 * - 'both'   — both of the above.
 * - 'off'    — neither (equivalent to `enabled: false` for mood purposes).
 */
export type SentimentProducerMode = 'server' | 'client' | 'both' | 'off';

export interface SentimentConfig {
  enabled?: boolean;
  sensitivity?: number;
  transitionDuration?: number;
  moodPersistence?: number;
  /** 0–1 ceiling on mood pose weights, applied AFTER personality scaling. */
  expressivenessCap?: number;
  /** Producer choice — see SentimentProducerMode. */
  producer?: SentimentProducerMode | string;
  keywords?: Record<string, string[]>;
}

/**
 * The resolved runtime defaults. Every value here is pinned to the behaviour
 * production had BEFORE the knobs became configurable (Aug 17 2026), so a
 * tenant that never touches the Mood Reactions panel sees zero change:
 * - sensitivity 0.3    = the old hardcoded `shouldChangeMood` gate
 * - transitionDuration = the old hardcoded 800ms in Avatar.tsx handleSentiment
 * - moodPersistence    = the old hardcoded decayAfterMs 8000
 * - expressivenessCap 1= no ceiling (morphs already clamp to [0,1])
 * - producer 'server'  = only `visual.sentiment` events drive the face
 */
export const SENTIMENT_RUNTIME_DEFAULTS = {
  enabled: true,
  sensitivity: 0.3,
  transitionDuration: 800,
  moodPersistence: 8000,
  expressivenessCap: 1,
  producer: 'server' as SentimentProducerMode,
} as const;

export interface SentimentRuntimeConfig {
  enabled: boolean;
  sensitivity: number;
  transitionDuration: number;
  moodPersistence: number;
  expressivenessCap: number;
  producer: SentimentProducerMode;
}

let runtimeConfig: SentimentRuntimeConfig = { ...SENTIMENT_RUNTIME_DEFAULTS };

// Active runtime patterns -- start with defaults, can be overridden by config
let activePatterns: Record<string, { keywords: string[]; weight: number }> | null = null;

function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

function normalizeProducer(value: unknown): SentimentProducerMode {
  return value === 'server' || value === 'client' || value === 'both' || value === 'off'
    ? value
    : SENTIMENT_RUNTIME_DEFAULTS.producer;
}

/**
 * Apply a sentiment settings section (from the avatar settings chain via
 * `avatar:config:update`, or a host prop). Each call re-resolves from the
 * production defaults + the provided section, so deleting a saved key
 * returns that knob to the default rather than sticking at a stale value.
 */
export function configureSentiment(config: SentimentConfig): void {
  const cfg = config ?? {};
  runtimeConfig = {
    enabled: cfg.enabled !== false,
    sensitivity: clampNumber(cfg.sensitivity, 0.05, 0.95, SENTIMENT_RUNTIME_DEFAULTS.sensitivity),
    transitionDuration: clampNumber(cfg.transitionDuration, 100, 5000, SENTIMENT_RUNTIME_DEFAULTS.transitionDuration),
    moodPersistence: clampNumber(cfg.moodPersistence, 500, 60000, SENTIMENT_RUNTIME_DEFAULTS.moodPersistence),
    expressivenessCap: clampNumber(cfg.expressivenessCap, 0, 1, SENTIMENT_RUNTIME_DEFAULTS.expressivenessCap),
    producer: normalizeProducer(cfg.producer),
  };

  if (!cfg.keywords || Object.keys(cfg.keywords).length === 0) {
    activePatterns = null;
    return;
  }
  activePatterns = {};
  for (const [sentiment, keywords] of Object.entries(cfg.keywords)) {
    const base = DEFAULT_SENTIMENT_PATTERNS[sentiment];
    activePatterns[sentiment] = {
      keywords: keywords.length > 0 ? keywords : (base?.keywords ?? []),
      weight: base?.weight ?? 1.0,
    };
  }
}

/** The resolved sentiment runtime config (defaults merged). */
export function getSentimentRuntimeConfig(): SentimentRuntimeConfig {
  return { ...runtimeConfig };
}

/** True when the on-device keyword analyzer may produce mood changes. */
export function isClientSentimentProducerActive(): boolean {
  return runtimeConfig.enabled
    && (runtimeConfig.producer === 'client' || runtimeConfig.producer === 'both');
}

/** True when server `visual.sentiment` events may drive the mood overlay. */
export function isServerSentimentConsumerActive(): boolean {
  return runtimeConfig.enabled
    && (runtimeConfig.producer === 'server' || runtimeConfig.producer === 'both');
}

/** 0–1 ceiling for mood overlay morph writes; read by moodAnimator. */
export function getSentimentExpressivenessCap(): number {
  return runtimeConfig.expressivenessCap;
}

/**
 * Confidence gates for server-produced `visual.sentiment` events, derived
 * from the sensitivity slider as a DELTA from the 0.3 default so the
 * default is bit-identical to the pre-knob behaviour:
 * - `minConfidence` — a first-application gate. 0 at default (every
 *   non-neutral label applied, as before); at low sensitivity (0.7) only
 *   emphatic emotion (score ≥ 0.4) moves the face.
 * - `replayThreshold` — the same-label re-trigger gate. 0.6 at default
 *   (the historical CONFIDENCE_REPLAY_THRESHOLD in Avatar.tsx).
 */
export function getServerSentimentGates(): { minConfidence: number; replayThreshold: number } {
  const delta = runtimeConfig.sensitivity - SENTIMENT_RUNTIME_DEFAULTS.sensitivity;
  return {
    minConfidence: Math.max(0, delta),
    replayThreshold: Math.max(0.3, Math.min(0.95, 0.6 + delta)),
  };
}

function getActivePatterns(): Record<string, { keywords: string[]; weight: number }> {
  return activePatterns ?? DEFAULT_SENTIMENT_PATTERNS;
}

// Default keyword patterns for sentiment detection
const DEFAULT_SENTIMENT_PATTERNS: Record<string, { keywords: string[]; weight: number }> = {
  positive: {
    keywords: [
      'thank', 'thanks', 'great', 'excellent', 'perfect', 'wonderful',
      'awesome', 'fantastic', 'love', 'appreciate', 'helpful', 'amazing',
      'good', 'nice', 'yes', 'sure', 'absolutely', 'definitely'
    ],
    weight: 1.0
  },

  negative: {
    keywords: [
      'terrible', 'awful', 'hate', 'angry', 'frustrated', 'disappointed'
    ],
    weight: 1.0
  },

  concerned: {
    keywords: [
      'problem', 'issue', 'error', 'fail', 'wrong', 'broken', 'bad',
      'not working', 'doesnt work', 'cant', 'worried', 'concern',
      'nervous', 'afraid', 'urgent', 'important', 'serious', 'critical'
    ],
    weight: 0.9
  },

  confused: {
    keywords: [
      'confused', 'understand', 'what', 'huh', 'pardon',
      'explain', 'unclear', 'dont get', 'not sure', 'help me understand',
      'do not understand', 'dont understand', 'what do', 'how do'
    ],
    weight: 1.2  // Higher weight to override negative
  },


  empathetic: {
    keywords: [
      'sorry to hear', 'understand how', 'that must', 'i appreciate',
      'i see', 'makes sense', 'i get it', 'totally understand'
    ],
    weight: 1.0
  },

  celebration: {
    keywords: [
      'hooray', 'yay', 'woohoo', 'congratulations', 'congrats', 'celebrate',
      'amazing', 'incredible', 'success', 'won', 'winner', 'champion',
      'best ever', 'so happy', 'finally', 'at last', 'made it', 'done it'
    ],
    weight: 1.3  // Higher weight - celebration is explicit
  },

  concentration: {
    keywords: [
      'thinking', 'processing', 'let me check', 'one moment', 'calculating',
      'analyzing', 'looking into', 'checking', 'searching', 'finding'
    ],
    weight: 0.8  // Lower weight - often system-triggered
  },

  surprised: {
    keywords: [
      'wow', 'whoa', 'oh my', 'oh no', 'unexpected', 'surprised', 'shocking',
      'really', 'seriously', 'unbelievable', 'no way', 'cant believe'
    ],
    weight: 1.1
  },

  frustrated: {
    keywords: [
      'frustrated', 'annoyed', 'angry', 'furious', 'ridiculous', 'unacceptable',
      'this is stupid', 'waste of time', 'fed up', 'had enough', 'give up',
      'useless', 'hopeless', 'never works', 'always broken', 'every time'
    ],
    weight: 1.5  // High weight - escalation trigger
  }
};

/**
 * Word-boundary keyword matching (Aug 16 2026).
 *
 * The analyzer previously used `String.includes`, so keywords matched
 * substrings inside unrelated words: 'won' in "wondering"/"won't" fired the
 * CELEBRATION mood mid-call — the "celebration smile while the user is
 * talking" Visual IVR defect. This is the same failure class that got the
 * runtime-service keyword scanners neutered on Jun 21 2026 ('no' in "know",
 * 'ok' in "look" — see .cursor/rules/avatar-sentiment-gesture.mdc).
 *
 * Matching rules:
 * - Text and keywords are lowercased and apostrophes are stripped, so
 *   "won't" normalizes to "wont" (which does NOT contain the word 'won')
 *   while "can't" → "cant" still matches the apostrophe-free keyword 'cant'.
 * - A keyword (or multi-word phrase) only matches between non-alphanumeric
 *   boundaries. No lookbehind — iOS Safari < 16.4 lacks it.
 */
const keywordRegexCache = new Map<string, RegExp>();

function normalizeForMatching(text: string): string {
  return text.toLowerCase().replace(/[’']/g, '');
}

function matchesKeyword(normalizedText: string, keyword: string): boolean {
  const normalizedKeyword = normalizeForMatching(keyword).trim();
  if (!normalizedKeyword) return false;
  let regex = keywordRegexCache.get(normalizedKeyword);
  if (!regex) {
    const escaped = normalizedKeyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    regex = new RegExp(`(?:^|[^a-z0-9])${escaped}(?:[^a-z0-9]|$)`);
    keywordRegexCache.set(normalizedKeyword, regex);
  }
  return regex.test(normalizedText);
}

/**
 * Analyze text for sentiment using keyword matching
 * @param text - The text to analyze (user or bot utterance)
 * @param previousSentiment - Previous sentiment for context
 * @returns Sentiment classification with confidence score
 */
export function analyzeSentiment(
  text: string,
  previousSentiment: Sentiment = 'neutral'
): SentimentResult {

  if (!text || text.trim().length === 0) {
    return { sentiment: 'neutral', confidence: 0, keywords: [] };
  }

  const normalizedText = normalizeForMatching(text);
  const scores: Record<string, { score: number; keywords: string[] }> = {
    positive: { score: 0, keywords: [] },
    negative: { score: 0, keywords: [] },
    confused: { score: 0, keywords: [] },
    concerned: { score: 0, keywords: [] },
    empathetic: { score: 0, keywords: [] },
    celebration: { score: 0, keywords: [] },
    concentration: { score: 0, keywords: [] },
    surprised: { score: 0, keywords: [] },
    frustrated: { score: 0, keywords: [] }
  };

  // Check for keyword matches (uses overridden patterns if configured).
  // Whole-word matches only — see matchesKeyword above.
  Object.entries(getActivePatterns()).forEach(([sentiment, pattern]) => {
    pattern.keywords.forEach(keyword => {
      if (matchesKeyword(normalizedText, keyword)) {
        scores[sentiment].score += pattern.weight;
        scores[sentiment].keywords.push(keyword);
      }
    });
  });

  // Find highest scoring sentiment
  let maxSentiment: Sentiment = 'neutral';
  let maxScore = 0;
  let matchedKeywords: string[] = [];

  Object.entries(scores).forEach(([sentiment, data]) => {
    if (data.score > maxScore) {
      maxScore = data.score;
      maxSentiment = sentiment as Sentiment;
      matchedKeywords = data.keywords;
    }
  });

  // Calculate confidence (0-1 scale)
  const confidence = Math.min(maxScore / 3, 1.0); // Normalize to 0-1

  // If low confidence, stick with previous sentiment or neutral.
  // The gate is the configured sensitivity (default 0.3 = the historical
  // hardcoded value): a LESS sensitive setting (higher value) needs a
  // stronger keyword signal before the detected sentiment stands.
  if (confidence < runtimeConfig.sensitivity) {
    return {
      sentiment: previousSentiment === 'neutral' ? 'neutral' : previousSentiment,
      confidence: 0.2,
      keywords: []
    };
  }

  return {
    sentiment: maxSentiment,
    confidence,
    keywords: matchedKeywords
  };
}

/**
 * Analyze conversation context for emotion
 * Looks at recent utterances for overall conversation mood
 * @param utterances - Array of recent utterances (limit to last 5-10)
 * @returns Overall conversation sentiment
 */
export function analyzeConversationMood(utterances: string[]): Sentiment {
  if (utterances.length === 0) return 'neutral';

  const sentiments = utterances.map(text => analyzeSentiment(text));

  // Count sentiment types
  const counts: Record<Sentiment, number> = {
    positive: 0,
    negative: 0,
    neutral: 0,
    confused: 0,
    concerned: 0,
    empathetic: 0,
    celebration: 0,
    concentration: 0,
    surprised: 0,
    frustrated: 0
  };

  sentiments.forEach(result => {
    counts[result.sentiment] += result.confidence;
  });

  // Return dominant sentiment
  let dominant: Sentiment = 'neutral';
  let maxCount = 0;

  Object.entries(counts).forEach(([sentiment, count]) => {
    if (count > maxCount) {
      maxCount = count;
      dominant = sentiment as Sentiment;
    }
  });

  return dominant;
}

/**
 * Determine if sentiment should trigger mood change
 * Prevents rapid mood switching
 * @param newSentiment - Newly detected sentiment
 * @param currentMood - Current avatar mood
 * @param confidence - Confidence of new sentiment
 * @returns Whether to change mood
 */
export function shouldChangeMood(
  newSentiment: Sentiment,
  currentMood: Sentiment,
  confidence: number
): boolean {
  // Same sentiment, no change
  if (newSentiment === currentMood) return false;

  // Low confidence, don't change. Configurable via the sensitivity slider;
  // default 0.3 (the historical hardcoded value, lowered from 0.5 long ago).
  if (confidence < runtimeConfig.sensitivity) return false;

  // Neutral is sticky (don't change to neutral easily)
  if (newSentiment === 'neutral' && confidence < 0.7) return false;

  // High confidence, change mood
  return true;
}

/**
 * Get recommended mood transition duration
 * Some moods should transition quickly, others slowly
 * @param fromMood - Current mood
 * @param toMood - Target mood
 * @returns Transition duration in milliseconds
 */
export function getMoodTransitionDuration(fromMood: Sentiment, toMood: Sentiment): number {
  // Instant transitions (immediate reactions)
  const instantTransitions: Array<[Sentiment, Sentiment]> = [
    ['neutral', 'surprised'],    // Surprise is instant
    ['positive', 'surprised'],
    ['concentration', 'surprised']
  ];

  if (instantTransitions.some(([from, to]) => from === fromMood && to === toMood)) {
    return 150; // 150ms - near instant
  }

  // Quick transitions (immediate reactions)
  const quickTransitions: Array<[Sentiment, Sentiment]> = [
    ['neutral', 'concerned'],
    ['neutral', 'confused'],
    ['positive', 'concerned'],
    ['neutral', 'celebration'],  // Celebration should be quick
    ['positive', 'celebration'],
    ['neutral', 'frustrated'],   // Frustration escalates quickly
    ['concerned', 'frustrated']
  ];

  if (quickTransitions.some(([from, to]) => from === fromMood && to === toMood)) {
    return 300; // 300ms
  }

  // Slow transitions (gradual mood changes)
  const slowTransitions: Array<[Sentiment, Sentiment]> = [
    ['concerned', 'positive'],
    ['negative', 'positive'],
    ['confused', 'neutral'],
    ['frustrated', 'positive'],  // De-escalation should be gradual
    ['frustrated', 'neutral'],
    ['frustrated', 'empathetic'], // Empathy response to frustration
    ['celebration', 'neutral']   // Coming down from celebration
  ];

  if (slowTransitions.some(([from, to]) => from === fromMood && to === toMood)) {
    return 1500; // 1.5s
  }

  // Concentration transitions (medium speed)
  if (toMood === 'concentration' || fromMood === 'concentration') {
    return 600; // 600ms - natural thinking transition
  }

  // Default moderate transition
  return 800; // 800ms
}

/**
 * Analyze text and return appropriate mood state
 * Convenience wrapper around analyzeSentiment with mood-specific logic
 * @param text - Text to analyze
 * @param currentMood - Current mood for context
 * @param isUserSpeaking - True if user speaking, false if avatar/bot
 * @returns Recommended mood state
 */
export function getMoodFromText(
  text: string,
  currentMood: Sentiment = 'neutral',
  isUserSpeaking: boolean = false
): { mood: Sentiment; confidence: number; shouldTransition: boolean } {

  const result = analyzeSentiment(text, currentMood);
  // Transition gate rides 0.05 below the sensitivity threshold (default
  // 0.3 - 0.05 = 0.25, the historical hardcoded value).
  const transitionGate = Math.max(0.05, runtimeConfig.sensitivity - 0.05);
  const shouldTransition = result.confidence > transitionGate && result.sentiment !== currentMood;

  // If user is speaking, be more conservative about changing mood
  // (avatar should react to user, not change mood on its own speech)
  if (!isUserSpeaking && result.confidence < 0.6) {
    return {
      mood: currentMood,
      confidence: 0,
      shouldTransition: false
    };
  }

  console.log('getMoodFromText:', {
    text: text.substring(0, 50),
    mood: result.sentiment,
    confidence: result.confidence,
    keywords: result.keywords,
    currentMood,
    shouldTransition
  });

  return {
    mood: result.sentiment,
    confidence: result.confidence,
    shouldTransition
  };
}

